import { useEffect, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
  Creator,
  MutationSignContractArgs,
  ContractStatus,
} from '../../gql/types'
import {
  SignContractResponse,
  SIGN_CONTRACT,
  GetActiveContractTemplateResponse,
  GET_ACTIVE_CONTRACT_TEMPLATE,
} from '../../gql/contracts'
import {
  Button,
  ButtonColor,
  NotificationType,
  Loader,
  PdfZoom,
  DownloadFileButton,
  Icon,
} from '../../components'
import { useNotification } from '../../utils/hooks'
import { collectBrowserFingerprint } from '../../utils/browserFingerprint'
import { AnalyticsEvent, trackEvent } from '../../utils/analytics'
import { IconType } from '../../components/Icon'

interface Props {
  creator: Creator
  onContractSigned: () => void
}

const ContractSigningPage = ({ creator, onContractSigned }: Props) => {
  const { addNotification } = useNotification()
  const [hasAgreed, setHasAgreed] = useState(false)

  // Helper function to check if the contract link is a PDF
  const isPdfLink = (url: string | undefined): boolean => {
    if (!url) return false
    return (
      url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('pdf')
    )
  }

  // Helper function to get filename from URL
  const getFilenameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url)
      const pathname = urlObj.pathname
      const filename = pathname.split('/').pop() || 'contract.pdf'
      return filename.includes('.pdf') ? filename : `${filename}.pdf`
    } catch {
      return 'contract.pdf'
    }
  }

  const {
    data: contractData,
    loading: contractLoading,
    error: contractError,
  } = useQuery<GetActiveContractTemplateResponse>(
    GET_ACTIVE_CONTRACT_TEMPLATE,
    {
      variables: {
        options: {
          filters: {
            statuses: [ContractStatus.ACTIVE],
          },
        },
      },
    },
  )

  const [signContract, { loading: signLoading }] = useMutation<
    SignContractResponse,
    MutationSignContractArgs
  >(SIGN_CONTRACT)

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewContractSigningPage)
  }, [])

  const handleSignContract = async () => {
    if (!hasAgreed) {
      addNotification(
        'Please read and accept the terms before continuing.',
        NotificationType.error,
      )
      return
    }

    try {
      // Collect browser fingerprint data for legal compliance
      const browserData = collectBrowserFingerprint()

      // Sign the contract with all necessary data
      await signContract({
        variables: {
          creatorId: creator.id,
          contractTemplateId:
            contractData?.getActiveCreatorContractTemplate?.id,
          input: {
            ...browserData, // All the browser fingerprint data
          },
        },
      })

      addNotification(
        'Contract signed successfully! You can now access your dashboard.',
        NotificationType.success,
      )
      trackEvent(AnalyticsEvent.contractSigned)

      // Call the callback to refresh creator data or redirect
      onContractSigned()
    } catch (error) {
      console.error('@error ContractSigningPage handleSignContract', error)
      addNotification(
        error.message || 'Failed to sign contract. Please try again.',
        NotificationType.error,
      )
    }
  }

  if (contractLoading) {
    return <Loader />
  }

  if (contractError) {
    console.log(contractError)
    return <p>There has been an error. Try refreshing the page</p>
  }

  const contract = contractData?.getActiveCreatorContractTemplate
  const hasPdfContract = contract?.link && isPdfLink(contract.link)

  return (
    <div className='min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-4xl mx-auto'>
        <div className='bg-white shadow-lg rounded-lg overflow-hidden'>
          {/* Header */}
          <div className='bg-primary-800 px-6 py-4'>
            <h1 className='text-2xl font-bold text-white'>
              Updated Terms of Service - Action Required
            </h1>
            <p className='text-blue-100 mt-2'>
              Hi {creator.firstName}! We've updated our terms of service. Please
              review and accept to continue using Clip.
            </p>
          </div>

          {/* Content */}
          <div className='px-6 py-8'>
            {/* <div className='mb-8'>
              <h2 className='text-xl font-semibold text-gray-900 mb-4'>
                Why do I need to sign this?
              </h2>
              <div className='bg-blue-50 border-l-4 border-blue-400 p-4 mb-6'>
                <div className='flex'>
                  <div className='ml-3'>
                    <p className='text-sm text-blue-700'>
                      <strong>Legal Requirement:</strong> To continue working
                      with brands on Clip, all creators must accept our updated
                      terms of service. This ensures compliance with current
                      laws and protects both you and the brands you work with.
                    </p>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Contract Text */}
            {contract && (
              <div className='mb-8'>
                <h3 className='text-lg font-semibold text-gray-900 mb-4'>
                  <div style={{ margin: '0 0 12px' }}>
                    <h2
                      id='cts-title'
                      style={{
                        fontSize: '1.25rem',
                        margin: '0 0 4px',
                      }}
                    >
                      Quick Summary of Creator Terms
                    </h2>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '.9rem',
                        color: '#555',
                      }}
                    >
                      This is a plain-English summary - see{' '}
                      <a
                        href={contract.link}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-blue-600 hover:text-blue-800 underline'
                      >
                        full contract
                      </a>{' '}
                      for details.
                    </p>
                  </div>
                </h3>
                <div className='borderrounded-lg overflow-y-auto'>
                  <div
                    className='prose prose-sm max-w-none text-gray-700'
                    dangerouslySetInnerHTML={{ __html: contract.text }}
                  />
                </div>
                <p
                  style={{
                    margin: '12px 0 0',
                    fontSize: '.8rem',
                    color: '#6b7280',
                  }}
                >
                  This summary is not a substitute for the contract. If anything
                  here conflicts with the full terms, the full terms control.
                </p>
                {contract.link && (
                  <p className='mt-2 text-sm text-gray-600'>
                    <a
                      href={contract.link}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-blue-600 hover:text-blue-800 underline'
                    >
                      View full terms document →
                    </a>
                  </p>
                )}
              </div>
            )}

            {/* Acceptance Checkbox */}
            <div className='mb-8'>
              <label className='flex items-start space-x-3 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={hasAgreed}
                  onChange={(e) => setHasAgreed(e.target.checked)}
                  className='mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <div className='text-sm'>
                  <span className='text-gray-900 font-medium'>
                    I have read, understood, and agree to the updated Terms of
                    Service
                  </span>
                  <p className='text-gray-600 mt-1'>
                    By checking this box, you confirm that you accept all terms
                    and conditions outlined above.
                  </p>
                </div>
              </label>
            </div>

            {/* Legal Notice */}
            <div className='bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-8'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <svg
                    className='h-5 w-5 text-yellow-400'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <h3 className='text-sm font-medium text-yellow-800'>
                    Legal Information
                  </h3>
                  <div className='mt-2 text-sm text-yellow-700'>
                    <p>
                      Your IP address, browser information, and other technical
                      details will be recorded for legal compliance and security
                      purposes when you sign this agreement.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className='flex flex-col sm:flex-row sm:justify-between gap-4'>
              <div className='text-sm text-gray-600'>
                <p>
                  <strong>Need help?</strong> Contact our support team at{' '}
                  <a
                    href='mailto:hello@useclip.com'
                    className='text-blue-600 hover:text-blue-800'
                  >
                    hello@useclip.com
                  </a>
                </p>
              </div>

              <div className='flex gap-3'>
                <Button
                  title='Accept Terms & Continue'
                  type='button'
                  colour={ButtonColor.primary}
                  onClick={handleSignContract}
                  loading={signLoading}
                  disabled={!hasAgreed || signLoading}
                  className='px-8'
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer Message */}
        <div className='mt-6 text-center'>
          <p className='text-sm text-gray-600'>
            Once you accept these terms, you'll unlock full access to your
            dashboard, campaigns, and earnings.
          </p>
        </div>
      </div>
    </div>
  )
}

export { ContractSigningPage }
